import { BehaviorSubject, EMPTY, Observable, ReplaySubject, Subject, Subscription, combineLatest, defer, of } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { AppType } from 'src/lib/fliko/enums/appName';
import { ArrHlp } from 'src/lib/fliko/helpers/array.helper';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { DefectSettingsData, DefectType } from 'src/lib/fliko/models/defects/defect-settings.data';
import { DefectData } from 'src/lib/fliko/models/defects/defect.data';
import { IssueType } from 'src/lib/fliko/models/defects/issue.base';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { Language } from 'src/lib/fliko/models/translation/generic';
import { CreateServiceUser } from 'src/lib/fliko/models/users/createdUser.data';
import { ServiceUserData } from 'src/lib/fliko/models/users/user.data';
import { AttHlp } from 'src/app/shared/helpers/attachment.helper';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { TranslationHelper } from 'src/app/shared/helpers/translation.helper';
import { EditedImageVM } from 'src/app/shared/models/editem-image.view-model';
import { idMap } from 'src/app/shared/operators/idMap.operator';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { WebNotificationService } from 'src/app/shared/services/web-notification.service';
import { CompanyApiService } from 'src/app/view-company/service/company-api.service';
import { EstateService } from 'src/app/view/estate-view/services/estate.service';
import { SubcontractorsDataService } from 'src/app/view/estate-view/services/subcontractors-data.service';
import { SubcontractorsService } from 'src/app/view/estate-view/services/subcontractors.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ResidentService } from 'src/app/view/users/services/resident.service';
import { UnitService } from 'src/app/view/users/services/unit.service';
import { UserService } from 'src/app/view/users/services/user.service';
import { DefectHelper } from '../helpers/defect.helper';
import { IssueService } from './issue.service';
import { AllDefectTypes, DefectTypeVM } from './models/defect-type.view-model';
import { DefectVM, DefectLocationType, DefectMessageVM, DefectConversationVM, IssueVM } from './models/defect-view-models';

@Injectable({
  providedIn: 'root'
})
export class DefectDataService implements OnDestroy {
  private defectVMs$ = new BehaviorSubject<DefectVM[] | null>(null);
  private issueVMs$ = new BehaviorSubject<IssueVM[] | null>(null);
  private messagesVMs$ = new Map<string, Observable<DefectConversationVM>>();
  private conversationEstateId?: string;
  private conversationEpoch = 0;
  private readonly conversationContextEnded$ = new Subject<void>();
  private readonly destroyed$ = new ReplaySubject<void>(1);
  private defectTypeVMs$: BehaviorSubject<AllDefectTypes> | null = null;

  private defectSettings$: BehaviorSubject<DefectSettingsData | null> | null = null;
  private defaultDefectTypes$: BehaviorSubject<DefectType[]> | null = null;

  private readonly locHelper = new LocalizationHelper(this.ls);

  private subscription$ = new Subscription();

  constructor(
    private issueService: IssueService,
    private estateService: EstateService,
    private webNtfService: WebNotificationService,
    private subcontractorsService: SubcontractorsService,
    private subcontractorsDS: SubcontractorsDataService,
    private ts: TranslationService,
    private userService: UserService,
    private residentService: ResidentService,
    private unitService: UnitService,
    private cas: CompanyApiService,
    private ls: LocalizationService,
    private ctxDS: ContextDataService,
    @Optional() @SkipSelf() thisService?: DefectDataService
  ) {
    if (thisService) throw new Error('Multiple instances of UsersDataService');
    this.subscription$.add(
      this.ctxDS.currentEstate$
        .pipe(map((estate) => estate?.id), distinctUntilChanged())
        .subscribe((estateId) => {
          this.conversationEstateId = estateId;
          this.conversationEpoch++;
          this.messagesVMs$.clear();
          this.conversationContextEnded$.next();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.conversationContextEnded$.complete();
    this.messagesVMs$.clear();
  }

  public getDefects$(ids?: string[]): Observable<DefectVM[]> {
    if (!this.defectVMs$.value) {
      this.initializeDefectData();
    }
    const initialized$: Observable<DefectVM[]> = this.defectVMs$.pipe(filter((x): x is DefectVM[] => x != null));
    return ids ? initialized$.pipe(map((arr) => arr?.filter((o) => ids.includes(o.data.id)))) : initialized$;
  }

  public getIssues$(ids?: string[]): Observable<IssueVM[]> {
    if (!this.issueVMs$.value) {
      this.initializeIssuesData();
    }
    const initialized$: Observable<IssueVM[]> = this.issueVMs$.pipe(filter((x): x is IssueVM[] => x != null));
    return ids ? initialized$.pipe(map((arr) => arr?.filter((o) => ids.includes(o.data.id)))) : initialized$;
  }

  public getDefectConversation$(defectId: string): Observable<DefectConversationVM> {
    // Old references stay expired even after returning to the same estate.
    const epoch = this.conversationEpoch;
    return defer(() => {
      if (this.subscription$.closed || !this.conversationEstateId || epoch !== this.conversationEpoch) return EMPTY;
      return this.messagesVMs$.get(defectId) ?? this.initializeConversationData(defectId);
    });
  }

  public getDefectTypeVMs$(): Observable<AllDefectTypes> {
    if (!this.defectTypeVMs$) {
      this.defectTypeVMs$ = new BehaviorSubject<AllDefectTypes>({ common: [], unit: [] });
      this.initializeDefectTypeVMs();
    }
    return this.defectTypeVMs$.asObservable();
  }

  public getDefectSettings$(): Observable<DefectSettingsData | null> {
    if (!this.defectSettings$) {
      this.defectSettings$ = new BehaviorSubject<DefectSettingsData | null>(null);
      this.subscription$.add(
        this.issueService.getDefectSettings$().subscribe((settings) => this.defectSettings$!.next(settings))
      );
    }
    return this.defectSettings$.asObservable();
  }

  public getDefaultDefectTypes$(): Observable<DefectType[]> {
    if (!this.defaultDefectTypes$) {
      this.defaultDefectTypes$ = new BehaviorSubject<DefectType[]>([]);
      this.subscription$.add(
        this.ctxDS.currentEstate$.subscribe((estate) => {
          if (estate) {
            this.cas.getCompanyDefectSettings(estate.companyId ?? '', estate.id).then((settings) => {
              if (settings) {
                const types = settings.defaultTypes;
                this.defaultDefectTypes$!.next(ArrHlp.merge(types.common, types.unit));
              }
            });
          }
        })
      );
    }
    return this.defaultDefectTypes$.asObservable();
  }

  private initializeDefectData(): void {
    this.subscription$.add(
      this.ls.onLanguageChanged$
        .pipe(
          switchMap((language) =>
            combineLatest([
              this.ctxDS.currentEstate$.pipe(
                switchMap((estate) =>
                  estate
                    ? combineLatest([of(estate), this.subcontractorsService.getCreateServiceUsers$(estate.id).pipe(idMap())])
                    : of([undefined, new Map<string, CreateServiceUser>()] as const)
                )
              ),
              this.issueService.getDefects$(),
              this.getDefectSettings$().pipe(map((x) => x?.defectType ?? []), idMap()), // prettier-ignore
              this.webNtfService.getDefectNtf$(),
              this.ts.getTitles$('defect', language as Language),
              combineLatest([
                this.userService.getUsers$().pipe(idMap()),
                this.subcontractorsService.getServiceUsers$().pipe(idMap()),
                this.unitService.getRentalUnitsMap$(),
                this.subcontractorsDS.getSubcontractorsMap$(),
                this.residentService.getResidents$()
              ])
            ])
          )
        )
        .subscribe(
          ([
            [estate, invites],
            defects,
            types,
            notifications,
            trTitles,
            [users, servUsers, units, subcontractors, residents]
          ]) => {
            const admins = MapHlp.idMap(this.ctxDS.peekEstateAdminUsers());
            const resById = MapHlp.idMap(residents);
            const resByUserId = MapHlp.idMap(residents, 'userId');
            const vms = defects.map((defect) => {
              const unit = MapHlp.get(units, defect.locationUnitId);
              const type = types.get(defect.typeId);
              const app = defect.reporterApp;

              const user = defect.userId ? (users.get(defect.userId) ?? servUsers.get(defect.userId)) : undefined;
              if (!user && defect.userId) console.warn(`User ${defect.userId} not found!`);
              return {
                data: defect,
                type,
                dataType: IssueType.DEFECT,
                typeName: type ? this.locHelper.defectTypeLabel(type) : defect.typeId,
                title: TranslationHelper.getTitle(defect, trTitles),

                unit,
                location: DefectHelper.getLocation(defect, estate?.type ?? EstateType.COMMUNITY, this.ls, unit),
                locationType: DefectLocationType.fromDefect(defect),

                user,
                admin: MapHlp.get(admins, defect.adminId ?? defect.userId), // adminId added in 1.5.6
                // eslint-disable-next-line max-len
                resident: MapHlp.get(resById, defect.residentId) ?? MapHlp.get(resByUserId, defect.userId), // some older defects has not resident id
                repSubcontractor: MapHlp.get(subcontractors, (user as ServiceUserData)?.subcontractorId),
                app,
                appName: this.ls.translate(app ? 'appName.' + app : 'label.noData'),

                servicer:
                  MapHlp.get(servUsers, defect.assignedServiceUserId) ?? MapHlp.get(invites, defect.assignedServiceUserId),
                assignedUser: MapHlp.get(users, defect.assignedUserId),
                servSubcontractor: MapHlp.get(subcontractors, defect.assignedSubcontractorId),

                images: this.getImages(defect),
                attachments: AttHlp.toFileInStorageVM(defect.attachments),
                updated: notifications ? !!notifications[defect.id] : false,
                webconId: defect.webconId
              };
            });
            this.defectVMs$!.next(vms);
          }
        )
    );
  }

  private getImages(defect: DefectData): EditedImageVM[] {
    const images: EditedImageVM[] = [];
    if (defect.photos) {
      for (const path of defect.photos.split('|')) {
        if (path) {
          const editKey = ParserHlp.sanitizeKey(ArrHlp.get(path.split('/'), -1) ?? '');
          const editPath = ObjHlp.get(defect.editedPhotos, editKey);

          images.push(EditedImageVM.fromPaths(editPath, path)!);
        }
      }
    }

    return images;
  }

  private initializeIssuesData(): void {
    this.subscription$.add(
      this.ls.onLanguageChanged$
        .pipe(
          switchMap((language) =>
            combineLatest([
              this.issueService.getIssues$(),
              this.userService.getUsers$().pipe(idMap()),
              this.residentService.getResidents$().pipe(idMap()),
              this.ts.getTitles$('residentIssue', language as Language)
            ])
          )
        )
        .subscribe(([issues, users, residents, trTitles]) => {
          const admins = MapHlp.idMap(this.ctxDS.peekEstateAdminUsers());
          const vms = issues.map((issue) => {
            return {
              data: issue,
              type: IssueType.ISSUE as const,
              dataType: IssueType.ISSUE,
              typeName: StrHlp.capitalize(this.ls.translate('issueType.residentIssue')),
              title: TranslationHelper.getTitle(issue, trTitles),

              user: MapHlp.get(users, issue.userId),
              admin: MapHlp.get(admins, issue.adminId),
              resident: MapHlp.get(residents, issue.residentId),
              assignedUser: MapHlp.get(users, issue.assignedUserId),
              repSubcontractor: undefined,
              app: AppType.CRM,
              appName: this.ls.translate('appType.web'),

              images: AttHlp.toFileInStorageData(issue.photos?.split('|')),
              attachments: AttHlp.toFileInStorageData(issue.attachments),
              updated: false as const
            };
          });
          this.issueVMs$!.next(vms);
        })
    );
  }

  private initializeConversationData(defectId: string): Observable<DefectConversationVM> {
    const removeFromCache = () => {
      if (this.messagesVMs$.get(defectId) === conversation$) this.messagesVMs$.delete(defectId);
    };
    const conversation$ = this.ls.onLanguageChanged$.pipe(
      switchMap((language) =>
        combineLatest([
          this.issueService.getMessages$(IssueType.DEFECT, defectId),
          this.estateService.getAdmins$(),
          this.ts.getConversation$('defectNote', defectId, language as Language),
          this.ts.getLanguages$('defectNote', defectId)
        ])
      ),
      map(([messages, admins, translations, languages]) => {
        const reporter: DefectMessageVM[] = [];
        const servicer: DefectMessageVM[] = [];

        messages.forEach((note) => {
          const propertyManager = !!admins.find((admin) => admin.userId === note.userId);
          const myself = this.ctxDS.peekCurrentUserId() === note.userId;
          (note.landlordCommunication ? reporter : servicer).push({
            ...note,
            propertyManager,
            myself,
            storageImages: AttHlp.toFileInStorageVM(note.images),
            language: ObjHlp.get(languages, note.id),
            translation: ObjHlp.get(translations, note.id)
          });
        });

        reporter.sort((a, b) => a.date - b.date);
        servicer.sort((a, b) => a.date - b.date);
        return { reporter, servicer };
      }),
      takeUntil(this.conversationContextEnded$),
      takeUntil(this.destroyed$),
      // Remove terminal entries before observers can reopen the same conversation.
      tap({ error: removeFromCache, complete: removeFromCache }),
      finalize(removeFromCache),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.messagesVMs$.set(defectId, conversation$);
    return conversation$;
  }

  private initializeDefectTypeVMs(): void {
    this.subscription$.add(
      combineLatest([
        this.ctxDS.currentEstate$,
        this.getDefectSettings$(),
        this.issueService.getDefects$(),
        this.subcontractorsService.getEstateSubcontractors$()
      ]).subscribe(async ([estate, defectSettings, defects, estateSubcontractors]) => {
        const common: DefectTypeVM[] = [];
        const unit: DefectTypeVM[] = [];

        const usedTypes = new Set<string>();

        const defectUsageMap = new Map<string, number>();
        defects.forEach((defect) => {
          const usage = defectUsageMap.get(defect.typeId) ?? 0;
          defectUsageMap.set(defect.typeId, usage + 1);
        });

        const subcontractorUsageMap = new Map<string, number>();
        estateSubcontractors.forEach((sub) => {
          sub.types?.forEach((type) => {
            const usage = subcontractorUsageMap.get(type.typeId) ?? 0;
            subcontractorUsageMap.set(type.typeId, usage + 1);
          });
        });

        defectSettings?.defectType.forEach((base) => {
          const defectUsage = defectUsageMap.get(base.id) ?? 0;
          const subcontractorUsage = subcontractorUsageMap.get(base.id) ?? 0;
          const temp: DefectTypeVM = {
            base,
            selected: true,
            fromCompany: false,
            defectUsage,
            subcontractorUsage,
            type: base.location == 'apartment' ? 'unit' : 'common'
          };
          if (temp.type == 'unit') unit.push(temp);
          else common.push(temp);
          usedTypes.add(base.id);
        });

        const process = (base: DefectType, type: 'common' | 'unit', tab: DefectTypeVM[]) => {
          if (usedTypes.has(base.id)) {
            tab.find((typeVM) => typeVM.base.id == base.id)!.fromCompany = true;
          } else {
            const defectUsage = defectUsageMap.get(base.id) ?? 0;
            const subcontractorUsage = subcontractorUsageMap.get(base.id) ?? 0;
            tab.push({ base, selected: false, fromCompany: true, defectUsage, subcontractorUsage, type });
          }
        };

        if (estate) {
          const companyDefectSettings = await this.cas.getCompanyDefectSettings(estate.companyId ?? '', estate.id);
          const companyTypes = companyDefectSettings?.defaultTypes;
          companyTypes?.common?.forEach((base) => process(base, 'common', common));
          if (estate?.type == EstateType.PRS) {
            companyTypes?.unit?.forEach((base) => process(base, 'unit', unit));
          }
        }

        this.defectTypeVMs$!.next({ common, unit });
      })
    );
  }
}
